import React, { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import {
	useForgotPasswordMutation,
	useGoogleCallbackMutation,
	useGoogleLoginMutation,
	useLookupResetTokenQuery,
	useMicrosoftLoginMutation,
	useResetPasswordMutation,
} from '../api/auth';
import BasysLoginForm from '../components/BasysLoginForm';
import ForgotPasswordForm from '../components/ForgotPasswordForm';
import LoginSignupForm from '../components/LoginSignupForm';
import Logo from '../components/Logo';
import ResetPasswordForm from '../components/ResetPasswordForm';
import { hasFeature } from '../hooks/useFeature';
import { useAuth } from '../providers/AppAuthProvider';
import { useTypedSelector } from '../store';
import {
	OAUTH_NONCE_KEY,
	OAUTH_PKCE_VERIFIER_KEY,
	oauthStateNonce,
	pkcePair,
} from '../utils/oauthReturnOrigin';

const LoginPage: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [isLogin, setIsLogin] = useState(true);
	const [showForgot, setShowForgot] = useState(false);
	const [showReset, setShowReset] = useState(false);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [name, setName] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);
	const [isResetSubmitting, setIsResetSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [successMessage, setSuccessMessage] = useState('');
	const [isGoogleLoading, setIsGoogleLoading] = useState(false);
	const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

	const { login, register, handleGoogleAuth } = useAuth();
	const [forgotPassword] = useForgotPasswordMutation();
	const [resetPassword] = useResetPasswordMutation();
	const [googleLogin] = useGoogleLoginMutation();
	const [googleCallback] = useGoogleCallbackMutation();
	const [microsoftLogin] = useMicrosoftLoginMutation();
	const navigate = useNavigate();

	// Get tenant from Redux state (set by TenantInitializer)
	const tenant = useTypedSelector((state) => state.tenant.tenant);
	const tenantLoading = useTypedSelector((state) => state.tenant.loading);
	// Accounts come from the tenant admin: no sign-up, plus Microsoft sign-in.
	const adminCreatedAccounts = hasFeature(tenant, 'admin_created_accounts');

	// Determine which logo to show
	// Show RocketFee logo only for app.rocketfee.com or rocketfee-dev.mightybot.ai
	// For all other domains, wait for tenant data to load, then show tenant logo if available
	const hostname = window.location.hostname;
	const isRocketFeeDomain =
		hostname === 'app.rocketfee.com' ||
		hostname === 'dev.rocketfee.com' ||
		hostname === 'rocketfee-dev.mightybot.ai';

	// Use tenant logo if available and not on RocketFee domain
	// For non-RocketFee domains, wait for tenant to load before showing logo
	const logoToShow = isRocketFeeDomain
		? null // Always show RocketFee logo for RocketFee domains
		: tenant
			? tenant.logo_base64 || null
			: undefined; // undefined = wait for tenant to load

	useEffect(() => {
		const type = (searchParams.get('type') || 'login').toLowerCase();
		setIsLogin(type !== 'signup' || adminCreatedAccounts);
		setShowForgot(type === 'forgot-password');
		setShowReset(type === 'reset-password');

		// Pre-fill email if provided in URL
		const prefillEmail = searchParams.get('email');
		if (prefillEmail) setEmail(decodeURIComponent(prefillEmail));
	}, [searchParams, adminCreatedAccounts]);

	const switchMode = (
		mode: 'login' | 'signup' | 'forgot-password' | 'reset-password',
	) => {
		setIsLogin(mode !== 'signup');
		setShowForgot(mode === 'forgot-password');
		setShowReset(mode === 'reset-password');
		const params = new URLSearchParams(searchParams.toString());
		params.set('type', mode);
		// Clear token when switching to login mode
		if (mode === 'login') {
			params.delete('token');
		}
		setSearchParams(params, { replace: true });
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError('');

		try {
			if (!isLogin) {
				if (password !== confirmPassword) {
					setError('Passwords do not match');
					setIsSubmitting(false);
					return;
				}
			}
			if (isLogin) {
				await login(email, password);
				// On login, redirect to dashboard
				navigate('/', { replace: true });
			} else {
				// Pass invited email along for backend gating when present
				const params = new URLSearchParams(searchParams.toString());
				const invitedEmail = params.get('email')
					? decodeURIComponent(params.get('email')!)
					: undefined;
				const result = await register(email, password, name, invitedEmail);

				if (result?.requires_verification) {
					const verifyParams = new URLSearchParams();
					verifyParams.set('pending', '1');
					if (email) verifyParams.set('email', email);

					navigate(`/verify-email?${verifyParams.toString()}`, {
						replace: true,
					});
				} else {
					if (result?.token) {
						navigate('/', { replace: true });
					} else {
						switchMode('login');
					}
				}
			}
		} catch (err: any) {
			setError(err.message || 'An error occurred');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleForgotSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsForgotSubmitting(true);
		setError('');
		setSuccessMessage('');
		try {
			const targetEmail = email;
			if (!targetEmail) {
				setError('Please enter your email');
				return;
			}
			const result = await forgotPassword({ email: targetEmail }).unwrap();
			if (result.success) {
				setSuccessMessage(
					result.message || 'If the email exists, a reset link has been sent.',
				);
			} else {
				setError(result.message || 'Unable to send reset link');
			}
		} catch (err: any) {
			setError('An error occurred');
		} finally {
			setIsForgotSubmitting(false);
		}
	};

	// Reset password flow
	const resetToken = searchParams.get('token') || '';
	const { data: resetLookup, isLoading: loadingLookup } =
		useLookupResetTokenQuery(resetToken, { skip: !showReset || !resetToken });

	const handleResetSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsResetSubmitting(true);
		setError('');
		setSuccessMessage('');
		try {
			if (!resetToken) {
				setError('Missing token');
				return;
			}
			if (password !== confirmPassword) {
				setError('Passwords do not match');
				return;
			}
			const res = await resetPassword({
				token: resetToken,
				new_password: password,
			}).unwrap();
			if (res.success) {
				setSuccessMessage(
					'Password reset successful. Taking you back to sign in...',
				);
				setTimeout(() => switchMode('login'), 2000);
			} else {
				setError(res.message || 'Invalid or expired token');
			}
		} catch (err: any) {
			setError(err?.data?.message || err.message || 'An error occurred');
		} finally {
			setIsResetSubmitting(false);
		}
	};

	const handleGoogleLogin = async () => {
		try {
			setIsGoogleLoading(true);
			setError('');

			// Store current URL (with subdomain) for redirect after OAuth
			// This preserves tenant subdomain context
			const currentUrl = window.location.origin + window.location.pathname;
			sessionStorage.setItem('oauth_redirect_url', currentUrl);

			// Get Google OAuth URL
			const result = await googleLogin().unwrap();
			if (result.success && result.auth_url) {
				// Remember this tab's nonce; the callback refuses a state that was not
				// started here (sessionStorage is per tab and per origin).
				const nonce = oauthStateNonce(result.state ?? null);
				if (nonce) sessionStorage.setItem(OAUTH_NONCE_KEY, nonce);
				// Redirect to Google OAuth in the same window
				window.location.href = result.auth_url;
				// Note: setIsGoogleLoading(false) not needed here as page redirects
			} else {
				setError('Failed to initiate Google login');
				setIsGoogleLoading(false);
			}
		} catch (err: any) {
			setError(err?.data?.message || err.message || 'Google login failed');
			setIsGoogleLoading(false);
		}
	};

	const handleMicrosoftLogin = async () => {
		try {
			setIsMicrosoftLoading(true);
			setError('');
			const { verifier, challenge } = await pkcePair();
			sessionStorage.setItem(OAUTH_PKCE_VERIFIER_KEY, verifier);
			const result = await microsoftLogin(challenge).unwrap();
			if (result.success && result.auth_url) {
				const nonce = oauthStateNonce(result.state ?? null);
				if (nonce) sessionStorage.setItem(OAUTH_NONCE_KEY, nonce);
				window.location.href = result.auth_url;
			} else {
				setError('Failed to initiate Microsoft login');
				setIsMicrosoftLoading(false);
			}
		} catch (err: any) {
			setError(err?.data?.message || err.message || 'Microsoft login failed');
			setIsMicrosoftLoading(false);
		}
	};

	const renderForm = () => {
		if (showForgot) {
			return (
				<ForgotPasswordForm
					forgotEmail={email}
					setForgotEmail={setEmail}
					loading={isForgotSubmitting}
					successMessage={successMessage}
					onSubmit={handleForgotSubmit}
					onBackToLogin={() => switchMode('login')}
				/>
			);
		}

		if (showReset) {
			return (
				<ResetPasswordForm
					password={password}
					setPassword={setPassword}
					confirmPassword={confirmPassword}
					setConfirmPassword={setConfirmPassword}
					loading={isResetSubmitting}
					loadingLookup={loadingLookup}
					error={error}
					successMessage={successMessage}
					userEmail={resetLookup?.email || ''}
					onSubmit={handleResetSubmit}
					onBackToLogin={() => switchMode('login')}
				/>
			);
		}

		return (
			<LoginSignupForm
				isLogin={isLogin}
				email={email}
				setEmail={setEmail}
				password={password}
				setPassword={setPassword}
				confirmPassword={confirmPassword}
				setConfirmPassword={setConfirmPassword}
				name={name}
				setName={setName}
				loading={isSubmitting}
				error={error}
				onSubmit={handleSubmit}
				onSwitchMode={switchMode}
				onForgotPassword={() => switchMode('forgot-password')}
				onGoogleLogin={handleGoogleLogin}
				isGoogleLoading={isGoogleLoading}
			/>
		);
	};

	if (adminCreatedAccounts && !showForgot && !showReset) {
		return (
			<BasysLoginForm
				logoBase64={tenant?.logo_base64 || null}
				tenantName={tenant?.name ?? ''}
				email={email}
				setEmail={setEmail}
				password={password}
				setPassword={setPassword}
				loading={isSubmitting}
				error={error}
				onSubmit={handleSubmit}
				onForgotPassword={() => switchMode('forgot-password')}
				onGoogleLogin={handleGoogleLogin}
				isGoogleLoading={isGoogleLoading}
				onMicrosoftLogin={
					tenant?.metadata?.microsoft_tenant_ids?.length
						? handleMicrosoftLogin
						: undefined
				}
				isMicrosoftLoading={isMicrosoftLoading}
			/>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
			<div className="sm:mx-auto sm:w-full sm:max-w-md">
				<div className="flex justify-center mb-6">
					{logoToShow !== undefined ? (
						<Logo logoBase64={logoToShow} alt={tenant?.name || 'RocketFee'} />
					) : (
						// Show placeholder while tenant is loading (same size as logo to prevent layout shift)
						<div className="h-10 w-auto" />
					)}
				</div>
				<div className="text-center">
					<h2 className="text-3xl font-bold text-gray-900">
						{showForgot
							? showReset
								? 'Reset your password'
								: 'Forgot your password?'
							: isLogin
								? 'Sign in to your account'
								: 'Create your account'}
					</h2>
					<p className="mt-2 text-sm text-gray-600">
						{!showForgot &&
							!showReset &&
							(isLogin ? (
								<>
									Or{' '}
									<button
										type="button"
										onClick={() => switchMode('signup')}
										className="font-medium text-purple-600 hover:text-purple-500"
									>
										create a new account
									</button>
								</>
							) : (
								<>
									Or{' '}
									<button
										type="button"
										onClick={() => switchMode('login')}
										className="font-medium text-purple-600 hover:text-purple-500"
									>
										sign in to your existing account
									</button>
								</>
							))}
					</p>
				</div>
			</div>

			<div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
				<div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
					{renderForm()}
				</div>
			</div>
		</div>
	);
};

export default LoginPage;
